import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import axios from 'axios';

const UserManagementEnhanced = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Advanced search and filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [sortField, setSortField] = useState('full_name');
  const [sortDirection, setSortDirection] = useState('asc');
  
  // Batch operations
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showBatchActions, setShowBatchActions] = useState(false);
  
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    full_name: '',
    first_name: '',
    last_name: '',
    phone: '',
    title: '',
    role: 'user'
  });
  const [resetPassword, setResetPassword] = useState('');

  const { user: currentUser, isAdmin } = useAuth();
  const API = process.env.REACT_APP_BACKEND_URL + '/api' || 'http://localhost:8001/api';

  // Redirect if not admin
  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Accès non autorisé</h2>
          <p className="text-gray-600">Vous n'avez pas les permissions nécessaires pour accéder à cette page.</p>
        </div>
      </div>
    );
  }

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    setShowBatchActions(selectedUsers.length > 0);
  }, [selectedUsers]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data);
    } catch (error) {
      setError('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  const reloadData = async () => {
    await fetchUsers();
    setSelectedUsers([]);
  };

  // Advanced search function
  const handleAdvancedSearch = async () => {
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('query', searchTerm);
      if (filterRole) params.append('role', filterRole);
      if (filterStatus) params.append('status', filterStatus);
      
      const response = await axios.get(`${API}/admin/users/search?${params}`);
      setUsers(response.data);
      setSelectedUsers([]);
    } catch (error) {
      console.error('Error in advanced search:', error);
      // Fallback to client-side filtering
      const filtered = getFilteredAndSortedUsers();
      setUsers(filtered);
    }
  };

  const resetFilters = () => {
    setSearchTerm('');
    setFilterRole('');
    setFilterStatus('');
    setSortField('full_name');
    setSortDirection('asc');
    setSelectedUsers([]);
    fetchUsers();
  };

  // Client-side filtering and sorting
  const getFilteredAndSortedUsers = () => {
    let filtered = users.filter(user => {
      const matchesSearch = !searchTerm || 
        user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesRole = !filterRole || user.role === filterRole;
      const matchesStatus = !filterStatus || 
        (filterStatus === 'active' && user.is_active) ||
        (filterStatus === 'inactive' && !user.is_active);
      
      return matchesSearch && matchesRole && matchesStatus;
    });

    // Sort users
    filtered.sort((a, b) => {
      let aValue = a[sortField] || '';
      let bValue = b[sortField] || '';
      
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (sortDirection === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

    return filtered;
  };

  // Selection handlers
  const handleSelectUser = (userId) => {
    setSelectedUsers(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const handleSelectAll = () => {
    const filteredUsers = getFilteredAndSortedUsers();
    if (selectedUsers.length === filteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map(user => user.id));
    }
  };

  // Batch operations
  const handleBatchDelete = async () => {
    if (selectedUsers.length === 0) return;
    
    // Prevent deleting current user
    const currentUserInSelection = selectedUsers.includes(currentUser.id);
    if (currentUserInSelection) {
      alert('Vous ne pouvez pas supprimer votre propre compte dans une action groupée.');
      return;
    }
    
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer ${selectedUsers.length} utilisateur(s) ?`)) {
      try {
        await axios.post(`${API}/admin/users/batch-delete`, {
          user_ids: selectedUsers
        });
        
        fetchUsers();
        setSelectedUsers([]);
        alert(`${selectedUsers.length} utilisateur(s) supprimé(s) avec succès!`);
      } catch (error) {
        console.error('Error in batch delete:', error);
        setError('Erreur lors de la suppression multiple');
      }
    }
  };

  const handleBatchStatusChange = async (isActive) => {
    if (selectedUsers.length === 0) return;
    
    try {
      await axios.post(`${API}/admin/users/batch-update-status`, {
        user_ids: selectedUsers,
        is_active: isActive
      });
      
      fetchUsers();
      setSelectedUsers([]);
      alert(`Statut de ${selectedUsers.length} utilisateur(s) modifié avec succès!`);
    } catch (error) {
      console.error('Error in batch status update:', error);
      setError('Erreur lors de la modification du statut');
    }
  };

  const handleBatchRoleChange = async (newRole) => {
    if (selectedUsers.length === 0) return;
    
    try {
      await axios.post(`${API}/admin/users/batch-update-role`, {
        user_ids: selectedUsers,
        role: newRole
      });
      
      fetchUsers();
      setSelectedUsers([]);
      alert(`Rôle de ${selectedUsers.length} utilisateur(s) modifié avec succès!`);
    } catch (error) {
      console.error('Error in batch role update:', error);
      setError('Erreur lors de la modification du rôle');
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');

    try {
      await axios.post(`${API}/admin/users`, formData);
      setShowCreateForm(false);
      setFormData({
        username: '',
        email: '',
        password: '',
        full_name: '',
        first_name: '',
        last_name: '',
        phone: '',
        title: '',
        role: 'user'
      });
      fetchUsers();
      alert('Utilisateur créé avec succès');
    } catch (error) {
      setError(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleUpdate = async (userId, updates) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, updates);
      fetchUsers();
      alert('Utilisateur mis à jour avec succès');
    } catch (error) {
      setError(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async (userId) => {
    if (userId === currentUser.id) {
      alert('Vous ne pouvez pas supprimer votre propre compte.');
      return;
    }
    
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?')) {
      try {
        await axios.delete(`${API}/admin/users/${userId}`);
        fetchUsers();
        alert('Utilisateur supprimé avec succès');
      } catch (error) {
        setError(error.response?.data?.detail || 'Erreur lors de la suppression');
      }
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setError('');

    try {
      await axios.post(`${API}/admin/users/${selectedUser.id}/reset-password`, {
        user_id: selectedUser.id,
        new_password: resetPassword
      });
      setShowPasswordReset(false);
      setSelectedUser(null);
      setResetPassword('');
      alert('Mot de passe réinitialisé avec succès');
    } catch (error) {
      setError(error.response?.data?.detail || 'Erreur lors de la réinitialisation');
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-red-100 text-red-800';
      case 'manager':
        return 'bg-blue-100 text-blue-800';
      case 'commercial':
        return 'bg-green-100 text-green-800';
      case 'operateur':
        return 'bg-orange-100 text-orange-800';
      case 'operateur_recouvrement':
        return 'bg-purple-100 text-purple-800';
      case 'comex':
        return 'bg-indigo-100 text-indigo-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleLabel = (role) => {
    const labels = {
      'admin': 'Administrateur',
      'manager': 'Manager',
      'commercial': 'Commercial',
      'operateur': 'Opérateur',
      'operateur_recouvrement': 'Opérateur de recouvrement',
      'comex': 'COMEX',
      'user': 'Utilisateur'
    };
    return labels[role] || role;
  };

  const getStatusColor = (isActive) => {
    return isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return '↕️';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const filteredUsers = getFilteredAndSortedUsers();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="spinner mb-4"></div>
          <p className="text-gray-600">Chargement des utilisateurs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Gestion des Utilisateurs</h1>
        <div className="flex gap-2">
          {/* Reload Data Button */}
          <button
            onClick={reloadData}
            disabled={loading}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-lg transition-colors text-sm border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Actualiser les données"
          >
            {loading ? (
              <div className="flex items-center">
                <div className="spinner-sm mr-1"></div>
                <span className="hidden sm:inline">Chargement...</span>
              </div>
            ) : (
              <div className="flex items-center">
                <span className="text-base mr-1">🔄</span>
                <span className="hidden sm:inline">Reload</span>
              </div>
            )}
          </button>
          
          {/* New User Button */}
          <button
            onClick={() => setShowCreateForm(true)}
            className="bg-baobab-primary text-white px-4 py-2 rounded-lg hover:bg-baobab-dark transition-colors"
          >
            + Nouvel Utilisateur
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-600">{error}</p>
          <button
            onClick={() => setError('')}
            className="mt-2 text-red-500 hover:text-red-700 text-sm"
          >
            Fermer
          </button>
        </div>
      )}

      {/* Advanced Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <h3 className="font-medium text-gray-900 mb-4 flex items-center">
          <span className="mr-2">🔍</span>
          Recherche Avancée
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div>
            <input
              type="text"
              placeholder="Nom, email, username..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary"
            />
          </div>
          
          <div>
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary"
            >
              <option value="">Tous les rôles</option>
              <option value="admin">Administrateur</option>
              <option value="manager">Manager</option>
              <option value="commercial">Commercial</option>
              <option value="operateur">Opérateur</option>
              <option value="operateur_recouvrement">Opérateur de recouvrement</option>
              <option value="comex">COMEX</option>
              <option value="user">Utilisateur</option>
            </select>
          </div>
          
          <div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary"
            >
              <option value="">Tous les statuts</option>
              <option value="active">Actifs</option>
              <option value="inactive">Inactifs</option>
            </select>
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={handleAdvancedSearch}
              className="flex-1 bg-baobab-primary text-white px-4 py-3 rounded-lg hover:bg-baobab-dark transition-colors text-sm"
            >
              🔍 Rechercher
            </button>
            <button
              onClick={resetFilters}
              className="bg-gray-500 text-white px-4 py-3 rounded-lg hover:bg-gray-600 transition-colors text-sm"
            >
              ↺ Reset
            </button>
          </div>
        </div>
        
        {/* Sort Options */}
        <div className="flex items-center gap-4 text-sm">
          <span className="text-gray-600">Trier par:</span>
          <button
            onClick={() => handleSort('full_name')}
            className={`flex items-center gap-1 px-3 py-1 rounded ${
              sortField === 'full_name' ? 'bg-baobab-primary text-white' : 'bg-gray-200'
            }`}
          >
            Nom {getSortIcon('full_name')}
          </button>
          <button
            onClick={() => handleSort('role')}
            className={`flex items-center gap-1 px-3 py-1 rounded ${
              sortField === 'role' ? 'bg-baobab-primary text-white' : 'bg-gray-200'
            }`}
          >
            Rôle {getSortIcon('role')}
          </button>
          <button
            onClick={() => handleSort('created_at')}
            className={`flex items-center gap-1 px-3 py-1 rounded ${
              sortField === 'created_at' ? 'bg-baobab-primary text-white' : 'bg-gray-200'
            }`}
          >
            Date {getSortIcon('created_at')}
          </button>
        </div>
      </div>

      {/* Batch Actions Bar */}
      {showBatchActions && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-blue-700 font-medium">
                {selectedUsers.length} utilisateur(s) sélectionné(s)
              </span>
              <button
                onClick={() => setSelectedUsers([])}
                className="text-blue-600 hover:text-blue-800 text-sm"
              >
                Tout désélectionner
              </button>
            </div>
            
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => handleBatchStatusChange(true)}
                className="bg-green-500 text-white px-3 py-2 rounded-lg hover:bg-green-600 transition-colors text-sm"
              >
                ✓ Activer
              </button>
              <button
                onClick={() => handleBatchStatusChange(false)}
                className="bg-orange-500 text-white px-3 py-2 rounded-lg hover:bg-orange-600 transition-colors text-sm"
              >
                ⏸ Désactiver
              </button>
              <button
                onClick={() => handleBatchRoleChange('user')}
                className="bg-gray-500 text-white px-3 py-2 rounded-lg hover:bg-gray-600 transition-colors text-sm"
              >
                👤 → Utilisateur
              </button>
              <button
                onClick={() => handleBatchRoleChange('commercial')}
                className="bg-green-500 text-white px-3 py-2 rounded-lg hover:bg-green-600 transition-colors text-sm"
              >
                💼 → Commercial
              </button>
              <button
                onClick={handleBatchDelete}
                className="bg-red-500 text-white px-3 py-2 rounded-lg hover:bg-red-600 transition-colors text-sm"
              >
                🗑 Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Créer un utilisateur</h3>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom complet *
                  </label>
                  <input
                    type="text"
                    value={formData.full_name}
                    onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                    required
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom d'utilisateur *
                  </label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({...formData, username: e.target.value})}
                    required
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Téléphone
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Titre/Poste
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rôle *
                  </label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({...formData, role: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                  >
                    <option value="user">Utilisateur</option>
                    <option value="commercial">Commercial</option>
                    <option value="operateur">Opérateur</option>
                    <option value="operateur_recouvrement">Opérateur de recouvrement</option>
                    <option value="comex">COMEX</option>
                    <option value="manager">Manager</option>
                    <option value="admin">Administrateur</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mot de passe *
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  required
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-baobab-primary text-white py-3 px-4 rounded-lg hover:bg-baobab-dark transition-colors"
                >
                  Créer
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="flex-1 bg-gray-500 text-white py-3 px-4 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Password Reset Modal */}
      {showPasswordReset && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              Réinitialiser le mot de passe - {selectedUser.full_name}
            </h3>
            <form onSubmit={handlePasswordReset} className="space-y-4">
              <input
                type="password"
                placeholder="Nouveau mot de passe"
                value={resetPassword}
                onChange={(e) => setResetPassword(e.target.value)}
                required
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary"
              />
              <div className="flex gap-3">
                <button
                  type="submit"
                  className="flex-1 bg-baobab-primary text-white py-2 px-4 rounded-lg hover:bg-baobab-dark transition-colors"
                >
                  Réinitialiser
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordReset(false);
                    setSelectedUser(null);
                    setResetPassword('');
                  }}
                  className="flex-1 bg-gray-500 text-white py-2 px-4 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Results */}
      <div className="flex justify-between items-center text-sm text-gray-600 mb-4">
        <span>
          {filteredUsers.length} utilisateur{filteredUsers.length > 1 ? 's' : ''} trouvé{filteredUsers.length > 1 ? 's' : ''}
        </span>
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
            onChange={handleSelectAll}
            className="rounded"
          />
          <span>Sélectionner tout</span>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-baobab-primary text-white">
                <th className="p-3 text-left w-10">
                  <input
                    type="checkbox"
                    checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                    onChange={handleSelectAll}
                    className="rounded"
                  />
                </th>
                <th className="p-3 text-left cursor-pointer hover:bg-baobab-dark" onClick={() => handleSort('full_name')}>
                  Nom complet {getSortIcon('full_name')}
                </th>
                <th className="p-3 text-left">Username</th>
                <th className="p-3 text-left">Email</th>
                <th className="p-3 text-left cursor-pointer hover:bg-baobab-dark" onClick={() => handleSort('role')}>
                  Rôle {getSortIcon('role')}
                </th>
                <th className="p-3 text-left">Statut</th>
                <th className="p-3 text-left">Dernière connexion</th>
                <th className="p-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => (
                <tr key={user.id} className={`border-b border-gray-200 hover:bg-gray-50 ${
                  selectedUsers.includes(user.id) ? 'bg-blue-50' : ''
                }`}>
                  <td className="p-3">
                    <input
                      type="checkbox"
                      checked={selectedUsers.includes(user.id)}
                      onChange={() => handleSelectUser(user.id)}
                      className="rounded"
                    />
                  </td>
                  <td className="p-3 font-medium">{user.full_name}</td>
                  <td className="p-3">{user.username}</td>
                  <td className="p-3">{user.email}</td>
                  <td className="p-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleColor(user.role)}`}>
                      {getRoleLabel(user.role)}
                    </span>
                  </td>
                  <td className="p-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(user.is_active)}`}>
                      {user.is_active ? 'Actif' : 'Inactif'}
                    </span>
                  </td>
                  <td className="p-3">
                    {user.last_login ? 
                      new Date(user.last_login).toLocaleDateString('fr-FR') : 
                      'Jamais'
                    }
                  </td>
                  <td className="p-3">
                    <div className="flex gap-1 flex-wrap">
                      <button
                        onClick={() => handleUpdate(user.id, { is_active: !user.is_active })}
                        className={`px-2 py-1 rounded text-xs ${
                          user.is_active 
                            ? 'bg-gray-500 text-white hover:bg-gray-600' 
                            : 'bg-green-500 text-white hover:bg-green-600'
                        }`}
                        title={user.is_active ? 'Désactiver' : 'Activer'}
                      >
                        {user.is_active ? '⏸' : '▶'}
                      </button>
                      <button
                        onClick={() => {
                          setSelectedUser(user);
                          setShowPasswordReset(true);
                        }}
                        className="px-2 py-1 rounded text-xs bg-blue-500 text-white hover:bg-blue-600"
                        title="Réinitialiser mot de passe"
                      >
                        🔑
                      </button>
                      {user.id !== currentUser.id && (
                        <button
                          onClick={() => handleDelete(user.id)}
                          className="px-2 py-1 rounded text-xs bg-red-500 text-white hover:bg-red-600"
                          title="Supprimer"
                        >
                          🗑
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {filteredUsers.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <div className="text-4xl mb-2">👥</div>
            <p>Aucun utilisateur trouvé</p>
            {(searchTerm || filterRole || filterStatus) && (
              <p className="text-sm mt-2">Essayez de modifier vos critères de recherche</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagementEnhanced;